// Chakra Imports
import {
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
  Flex,
  Icon,
  Badge,
  useColorModeValue,
  Box,
} from '@chakra-ui/react';
import PropTypes from 'prop-types';
import React, { useState, useEffect } from 'react';
import { MdNotificationsNone, MdInfoOutline, MdShoppingCart, MdWarning } from 'react-icons/md';
import io from 'socket.io-client';
import { useNavigate } from 'react-router-dom';

export default function NavbarNotificationAdmin(props) {
  const { shadow, menuBg, textColor, hoverBg, iconColor, textColorSecondary, unreadBg } = props;
  const [notifications, setNotifications] = useState([]);
  const navigate = useNavigate();

  // Socket.IO bağlantısı ve bildirim dinleme
  useEffect(() => {
    const socket = io(import.meta.env.REACT_APP_API_URL || process.env.REACT_APP_API_URL || 'http://localhost:5001', {
      transports: ['websocket']
    });

    socket.on('connect', () => {
      const restaurantId = localStorage.getItem('restaurantId');
      if (restaurantId) {
        socket.emit('join_restaurant', restaurantId);
      }
    });

    // Yeni sipariş bildirimi
    socket.on('newOrderReceived', (data) => {
      
      // Eğer data sadece orderId, restaurantId, status içeriyorsa (orderUpdated event'i), görmezden gel
      if (data.orderId && !data.order && !data.tableName) {
        return;
      }
      
      const order = data.order || data;
      
      // Eğer order objesi yoksa veya _id yoksa, görmezden gel
      if (!order || !order._id) {
        return;
      }
      
      const tableName = order?.tableName || order?.tableId?.name || 'Bilinmeyen';
      
      // Aynı sipariş için tekrar bildirim gönderme (duplicate kontrolü)
      setNotifications(prev => {
        const exists = prev.some(n => 
          n.type === 'order' && 
          n.data?._id === order?._id
        );
        if (exists) {
          return prev;
        }
        
        const notification = {
          id: Date.now() + Math.random(),
          type: 'order',
          title: 'Yeni Sipariş',
          message: `${tableName} için yeni sipariş geldi`,
          timestamp: new Date(),
          read: false,
          data: order,
          route: '/admin/siparisler'
        };
        return [notification, ...prev];
      });
    });

    // Kritik stok bildirimi
    socket.on('lowStockAlert', (data) => {
      
      const notification = {
        id: Date.now() + Math.random(),
        type: 'stock',
        title: 'Kritik Stok Uyarısı',
        message: `${data.product?.name || 'Bir ürün'}'ün stoğu kritik seviyede`,
        timestamp: new Date(),
        read: false,
        data: data.product || data,
        route: '/admin/stok-durumu'
      };
      addNotification(notification);
    });

    // Genel bildirimler
    socket.on('businessNotification', (data) => {
      
      const notification = {
        id: Date.now() + Math.random(),
        type: data.type || 'general',
        title: data.title || 'Bildirim',
        message: data.message || '',
        timestamp: new Date(),
        read: false,
        data: data.data || {},
        route: data.route || null
      };
      addNotification(notification);
    });

    return () => {
      socket.disconnect();
    };
  }, []);

  // Bildirim ekleme fonksiyonu
  const addNotification = (notification) => {
    setNotifications(prev => [notification, ...prev]);
  };

  // Bildirim okundu olarak işaretle ve yönlendir
  const markAsRead = (notification) => {
    
    // Bildirimi listeden kaldır (data da siliniyor)
    setNotifications(prev => {
      const filtered = prev.filter(n => n.id !== notification.id);
      return filtered;
    });
    
    // Eğer route varsa yönlendir
    if (notification.route) {
      // Eğer route /admin/ ile başlıyorsa ve business slug içermiyorsa ekle
      if (notification.route.startsWith('/admin/') && !notification.route.match(/^\/admin\/[^/]+\//)) {
        const { getBusinessSlug } = require('../../utils/slugify');
        const businessSlug = getBusinessSlug();
        if (businessSlug) {
          const pathWithoutAdmin = notification.route.replace('/admin', '');
          navigate(`/admin/${businessSlug}${pathWithoutAdmin}`);
        } else {
          navigate(notification.route);
        }
      } else {
        navigate(notification.route);
      }
    }
  };

  // Tüm bildirimleri temizle
  const clearAllNotifications = () => {
    setNotifications([]);
  };

  return (
    <Menu>
      <MenuButton p='0px' position='relative'>
        <Icon
          mt='6px'
          as={MdNotificationsNone}
          color={iconColor}
          w='18px'
          h='18px'
          me='10px'
        />
        {notifications.length > 0 && (
          <Badge
            position='absolute'
            top='-2px'
            right='2px'
            bg='red.500'
            color='white'
            borderRadius='full'
            fontSize='10px'
            minW='18px'
            h='18px'
            display='flex'
            alignItems='center'
            justifyContent='center'
            px='4px'
          >
            {notifications.length > 9 ? '9+' : notifications.length}
          </Badge>
        )}
      </MenuButton>
      <MenuList
        boxShadow={shadow}
        p='16px'
        borderRadius='20px'
        bg={menuBg}
        border='none'
        mt='22px'
        me={{ base: '30px', md: 'unset' }}
        minW={{ base: 'unset', md: '380px', xl: '420px' }}
        maxW={{ base: '360px', md: 'unset' }}>
        <Flex justify='space-between' w='100%' mb='12px'>
          <Text fontSize='md' fontWeight='600' color={textColor}>
            Bildirimler
          </Text>
          {notifications.length > 0 && (
            <Text
              fontSize='sm'
              fontWeight='500'
              color={iconColor}
              ms='auto'
              cursor='pointer'
              onClick={clearAllNotifications}
              _hover={{ textDecoration: 'underline' }}>
              Tümünü Temizle
            </Text>
          )}
        </Flex>
        <Flex flexDirection='column' maxH='350px' overflowY='auto'>
          {notifications.length === 0 ? (
            <Text fontSize='xs' color={textColor} textAlign='center' py={3}>
              Bildirim yok
            </Text>
          ) : (
            notifications.map((notification) => (
              <MenuItem
                key={notification.id}
                _hover={{ bg: hoverBg }}
                _focus={{ bg: 'none' }}
                px='6px'
                py='4px'
                borderRadius='6px'
                mb='4px'
                bg={!notification.read ? unreadBg : 'transparent'}
                onClick={() => markAsRead(notification)}
                cursor='pointer'
              >
                <Flex
                  justify='center'
                  align='center'
                  borderRadius='8px'
                  minH='32px'
                  h='32px'
                  minW='32px'
                  w='32px'
                  me='8px'
                  bg={
                    notification.type === 'stock'
                      ? 'linear-gradient(135deg, #FF6B6B 0%, #EE5A6F 100%)'
                      : notification.type === 'order'
                      ? 'linear-gradient(135deg, #4ECDC4 0%, #44A08D 100%)'
                      : 'linear-gradient(135deg, #868CFF 0%, #4318FF 100%)'
                  }
                >
                  <Icon
                    as={notification.type === 'stock' ? MdWarning : notification.type === 'order' ? MdShoppingCart : MdInfoOutline}
                    color='white'
                    w={3.5}
                    h={3.5}
                  />
                </Flex>
                <Flex flexDirection='column' flex={1} minW={0}>
                  <Text
                    mb='1px'
                    fontWeight='600'
                    color={textColor}
                    fontSize='11px'
                    noOfLines={1}
                    lineHeight='1.2'
                  >
                    {notification.title}
                  </Text>
                  <Text
                    fontSize='9px'
                    color={textColorSecondary}
                    noOfLines={1}
                    lineHeight='1.2'
                  >
                    {notification.message}
                  </Text>
                  <Text
                    fontSize='9px'
                    color='gray.500'
                    mt='1px'
                    lineHeight='1.2'
                  >
                    {notification.timestamp.toLocaleTimeString('tr-TR', {
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </Text>
                </Flex>
              </MenuItem>
            ))
          )}
        </Flex>
      </MenuList>
    </Menu>
  );
}

NavbarNotificationAdmin.propTypes = {
  shadow: PropTypes.string,
  menuBg: PropTypes.string,
  textColor: PropTypes.string,
  hoverBg: PropTypes.string,
  iconColor: PropTypes.string,
  textColorSecondary: PropTypes.string,
  unreadBg: PropTypes.string,
};

